/**
 * ClipprProvider.tsx - React Context Provider for Clippr
 * 
 * CHANGE HISTORY:
 * - 251102_1210 [pending] danielbeer: Created React context for Clippr rebuild
 * 
 * React context provider for sharing Clippr instance across components.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { ClipprManager } from '../core/ClipprManager';
import type { ClipprConfig } from '../types';

export interface ClipprContextValue {
  clippr: ClipprManager;
}

const ClipprContext = createContext<ClipprContextValue | null>(null);

export interface ClipprProviderProps {
  children: React.ReactNode;
  clippr?: ClipprManager;
  config?: ClipprConfig;
}

/**
 * Clippr Context Provider
 * Provides Clippr instance to all child components
 */
export function ClipprProvider({ children, clippr, config }: ClipprProviderProps) {
  const manager = useMemo(() => {
    if (clippr) {
      return clippr;
    }
    return new ClipprManager(config);
  }, [clippr, config]);
  
  const value = useMemo(() => ({
    clippr: manager
  }), [manager]);
  
  return (
    <ClipprContext.Provider value={value}>
      {children}
    </ClipprContext.Provider>
  );
}

/**
 * Hook to access Clippr from context
 */
export function useClipprContext(): ClipprManager {
  const context = useContext(ClipprContext);
  
  if (!context) {
    throw new Error('useClipprContext must be used within ClipprProvider');
  }
  
  return context.clippr;
}

/**
 * Hook to check if within ClipprProvider
 */
export function useHasClipprContext(): boolean {
  const context = useContext(ClipprContext);
  return context !== null;
}

export default ClipprContext;
